import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { REPORT_MODE } from "../config";

const emptyPlayer = { name: "", number: "" };

const RULES = {
  basket: { titulaires: 3, remplacants: 1, max: 4, label: "Basket 3v3" },
  foot: { titulaires: 5, remplacants: 2, max: 7, label: "Football 5v5" },
};

export default function Inscription() {
  const [form, setForm] = useState({
    name: "",
    quartier: "",
    discipline: "basket",
    captain_name: "",
    captain_phone: "",
    captain_email: "",
    notes: "",
  });
  const rule = RULES[form.discipline];
  const [players, setPlayers] = useState(() => Array.from({ length: 3 }, () => ({ ...emptyPlayer })));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [regStatus, setRegStatus] = useState(null);

  useEffect(() => {
    api.get("/registration-status").then((r) => setRegStatus(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updatePlayer = (i, k, v) =>
    setPlayers((p) => p.map((pp, idx) => (idx === i ? { ...pp, [k]: v } : pp)));
  const addPlayer = () => {
    if (players.length >= rule.max) {
      toast.error(`Maximum ${rule.max} joueurs en ${rule.label} (${rule.titulaires} titulaires + ${rule.remplacants} remplaçant${rule.remplacants > 1 ? "s" : ""})`);
      return;
    }
    setPlayers((p) => [...p, { ...emptyPlayer }]);
  };
  const removePlayer = (i) => setPlayers((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        captain_email: form.captain_email || undefined,
        players: players.filter((p) => p.name.trim()),
      };
      await api.post("/teams", payload);
      setSuccess(true);
      toast.success("Équipe inscrite ! L'organisation vous recontactera.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (REPORT_MODE) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-24 text-center" data-testid="registration-postponed">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FFB800] mb-4">Événement reporté</p>
        <h1 className="font-display text-5xl sm:text-7xl leading-none mb-6">INSCRIPTIONS SUSPENDUES</h1>
        <p className="text-white/70 text-lg mb-4">
          Suite à la décision de la Préfecture (vigilance rouge canicule), l'Urban Fest est <strong className="text-[#FFB800]">reporté</strong>.
          Les inscriptions déjà enregistrées restent <strong>valables</strong>.
        </p>
        <p className="text-white/70 text-lg mb-10">
          Les inscriptions rouvriront dès l'annonce de la nouvelle date. Restez connectés !
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <a
            href={`https://wa.me/33695622959?text=${encodeURIComponent("Bonjour, j'ai une question concernant le report de l'Urban Fest.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brutal"
            data-testid="postponed-whatsapp-btn"
          >
            Une question ? WhatsApp : 06 95 62 29 59
          </a>
          <Link to="/" className="btn-brutal btn-brutal-ghost" data-testid="postponed-home-btn">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-24 text-center" data-testid="registration-success">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Confirmation</p>
        <h1 className="font-display text-5xl sm:text-7xl leading-none mb-6">INSCRIPTION ENVOYÉE</h1>
        <p className="text-white/70 text-lg mb-8">
          L'équipe <span className="text-[#FF6B1A] font-bold">{form.name}</span> a bien été enregistrée. L'organisation d'Action Impact Jeunesse vous contactera très vite au numéro indiqué.
        </p>
        <button
          onClick={() => {
            setSuccess(false);
            setForm({ name: "", quartier: "", discipline: "basket", captain_name: "", captain_phone: "", captain_email: "", notes: "" });
            setPlayers([{ ...emptyPlayer }, { ...emptyPlayer }, { ...emptyPlayer }]);
          }}
          className="btn-brutal"
          data-testid="new-registration-btn"
        >
          Inscrire une autre équipe
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-20" data-testid="inscription-page">
      <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Inscription équipe</p>
      <h1 className="font-display text-5xl sm:text-7xl leading-none mb-4">REJOINS<br />LA COMPÉT'</h1>
      <p className="text-white/60 max-w-xl mb-6 text-lg">
        Gratuit. Par quartier. Avec ton crew. Choisis ta discipline, complète les infos.
      </p>

      {/* Registration status */}
      {regStatus && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="registration-status">
          {["basket", "foot"].map((d) => {
            const s = regStatus[d];
            const label = d === "basket" ? "Basket 3v3" : "Football 5v5";
            return (
              <div
                key={d}
                className={`uf-card p-4 border-l-4 ${s.is_full ? "border-l-[#FF3B30]" : "border-l-[#FF6B1A]"}`}
                data-testid={`status-${d}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-white/50">{label}</p>
                    <p className="font-display text-2xl">
                      {s.count}
                      <span className="text-white/40">/{s.limit}</span>
                    </p>
                  </div>
                  <span
                    className={`text-xs tracking-widest uppercase font-bold px-3 py-1 rounded-full ${
                      s.is_full
                        ? "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40"
                        : "bg-[#FF6B1A]/15 text-[#FF6B1A] border border-[#FF6B1A]/40"
                    }`}
                  >
                    {s.is_full ? "Complet" : `${s.remaining} places`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Notice mineurs */}
      <div className="uf-card p-5 mb-12 border-l-4 border-l-[#FF6B1A] flex items-start gap-4 flex-wrap" data-testid="minors-notice">
        <span className="text-3xl">🔞</span>
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B1A] mb-1">Joueurs mineurs</p>
          <p className="text-white/85 text-sm">
            Tout joueur de moins de 18 ans doit présenter une <strong>autorisation parentale signée</strong> le jour du tournoi (à apporter au check-in).
          </p>
        </div>
        <Link to="/autorisation" className="btn-brutal !text-xs !py-2 !px-4" data-testid="autorisation-link">
          Télécharger le formulaire
        </Link>
      </div>

      <form onSubmit={submit} className="space-y-10" data-testid="registration-form">
        {/* Section équipe */}
        <section className="uf-card p-6 sm:p-8">
          <h2 className="font-display text-2xl mb-6">01 · L'ÉQUIPE</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nom de l'équipe *">
              <input required className="uf-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-team-name" />
            </Field>
            <Field label="Quartier *">
              <input required className="uf-input" value={form.quartier} onChange={(e) => update("quartier", e.target.value)} data-testid="input-quartier" placeholder="Ex: Zola, Pyramides…" />
            </Field>
            <Field label="Discipline *">
              <select required className="uf-input" value={form.discipline} onChange={(e) => update("discipline", e.target.value)} data-testid="select-discipline">
                <option value="basket" disabled={regStatus?.basket?.is_full}>
                  Basket 3x3{regStatus?.basket?.is_full ? " — Complet" : ""}
                </option>
                <option value="foot" disabled={regStatus?.foot?.is_full}>
                  Football 5x5{regStatus?.foot?.is_full ? " — Complet" : ""}
                </option>
              </select>
            </Field>
          </div>
          <div className="mt-5 flex items-start gap-3 p-4 border border-[#FF6B1A]/30 bg-[#FF6B1A]/5 rounded-lg" data-testid="discipline-rule">
            <span className="text-2xl">ℹ️</span>
            <div className="text-sm">
              <p className="font-bold text-white">Composition autorisée — {rule.label}</p>
              <p className="text-white/70">
                {rule.titulaires} titulaires + <span className="text-[#FF6B1A] font-bold">{rule.remplacants} remplaçant{rule.remplacants > 1 ? "s" : ""}</span> ({rule.max} joueurs maximum).
              </p>
            </div>
          </div>
        </section>

        {/* Capitaine */}
        <section className="uf-card p-6 sm:p-8">
          <h2 className="font-display text-2xl mb-6">02 · LE CAPITAINE</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nom complet *">
              <input required className="uf-input" value={form.captain_name} onChange={(e) => update("captain_name", e.target.value)} data-testid="input-captain-name" />
            </Field>
            <Field label="Téléphone *">
              <input required className="uf-input" value={form.captain_phone} onChange={(e) => update("captain_phone", e.target.value)} data-testid="input-captain-phone" placeholder="06 XX XX XX XX" />
            </Field>
            <Field label="Email (optionnel)">
              <input type="email" className="uf-input" value={form.captain_email} onChange={(e) => update("captain_email", e.target.value)} data-testid="input-captain-email" />
            </Field>
          </div>
        </section>

        {/* Joueurs */}
        <section className="uf-card p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-2xl">03 · LE ROSTER</h2>
            <button
              type="button"
              onClick={addPlayer}
              disabled={players.length >= rule.max}
              className="text-xs tracking-widest uppercase text-[#FF6B1A] hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="add-player-btn"
            >
              + Ajouter un joueur ({players.length}/{rule.max})
            </button>
          </div>
          <p className="text-sm text-white/60 mb-4">
            {rule.titulaires} titulaires + <span className="text-[#FF6B1A] font-bold">{rule.remplacants} remplaçant{rule.remplacants > 1 ? "s" : ""}</span> — soit {rule.max} joueurs max.
          </p>
          <div className="space-y-3">
            {players.map((p, i) => {
              const isSub = i >= rule.titulaires;
              return (
                <div key={i} className="grid grid-cols-[auto_1fr_110px_auto] gap-2 sm:gap-3 items-center">
                  <span
                    className={`text-[10px] tracking-widest uppercase font-bold w-20 text-center py-2 border ${
                      isSub
                        ? "border-[#FF6B1A]/40 text-[#FF6B1A] bg-[#FF6B1A]/5"
                        : "border-white/20 text-white/70"
                    }`}
                    data-testid={`player-role-${i}`}
                  >
                    {isSub ? "REMPL." : `TITUL. ${i + 1}`}
                  </span>
                  <input
                    className="uf-input"
                    placeholder={`Joueur ${i + 1}`}
                    value={p.name}
                    onChange={(e) => updatePlayer(i, "name", e.target.value)}
                    data-testid={`input-player-name-${i}`}
                  />
                  <input
                    className="uf-input"
                    placeholder="N°"
                    value={p.number}
                    onChange={(e) => updatePlayer(i, "number", e.target.value)}
                    data-testid={`input-player-number-${i}`}
                  />
                  {players.length > 1 ? (
                    <button type="button" onClick={() => removePlayer(i)} className="text-[#FF3B30] text-xs uppercase tracking-widest px-2" data-testid={`remove-player-${i}`}>
                      Retirer
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Notes */}
        <section className="uf-card p-6 sm:p-8">
          <h2 className="font-display text-2xl mb-6">04 · INFOS COMPLÉMENTAIRES</h2>
          <Field label="Un message pour l'organisation ?">
            <textarea rows={4} className="uf-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="input-notes" />
          </Field>
        </section>

        <div className="flex flex-wrap gap-4 items-center">
          <button type="submit" disabled={loading} className="btn-brutal" data-testid="submit-registration-btn">
            {loading ? "Envoi en cours…" : "Valider l'inscription"}
          </button>
          <p className="text-white/50 text-sm">Inscription gratuite — validation sous 48h.</p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
